import React, { useEffect, useState } from "react";
import { Box, Card, Stack, Title, Space } from "@mantine/core";
import { useParams } from "react-router-dom";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { PageProps } from "@sdflc/react-router-pages";

import {
  DisplayActivation,
  DisplayActivationService,
  Display,
  DisplayService,
} from "@/utils/api";

import { DashboardLayout } from "@/components/layouts/DashboardLayout";

import { ConfirmModal } from "@/components/generic";
import {
  DisplayLinkError,
  DisplayHeader,
  DisplayActivationsTable,
} from "@/components/features/";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const DisplayTvsPage: React.FC<PageProps> = (props: PageProps) => {
  const { displayId } = useParams<{ displayId: string }>();

  // Display state
  const [display, setDisplay] = useState<Display | null>(null);
  const [displayLoading, setDisplayLoading] = useState(false);
  const [displayError, setDisplayError] = useState<string | null>(null);

  // Activations state
  const [activations, setActivations] = useState<DisplayActivation[]>([]);
  const [activationsLoading, setActivationsLoading] = useState(false);
  const [_, setActivationsError] = useState<string | null>(null);

  // Modal state
  const [activationToRemove, setActivationToRemove] =
    useState<DisplayActivation | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Load display data
  const loadDisplay = async (id: string) => {
    setDisplayLoading(true);
    setDisplayError(null);

    try {
      const result = await DisplayService.list(
        { id: [id] },
        { page: 1, pageSize: 1 },
        []
      );

      if (
        result.code === OP_RESULT_CODES.OK &&
        result.data &&
        result.data.length > 0
      ) {
        setDisplay(result.data[0]);
      } else {
        setDisplayError("Display not found");
        setDisplay(null);
      }
    } catch (error) {
      setDisplayError(
        error instanceof Error ? error.message : "Failed to load display"
      );
      setDisplay(null);
    } finally {
      setDisplayLoading(false);
    }
  };

  // Load activations data
  const loadActivations = async (id: string) => {
    setActivationsLoading(true);
    setActivationsError(null);

    try {
      const result = await DisplayActivationService.list(
        { displayId: [id] },
        { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        [{ name: "createdAt", order: "DESC" }]
      );

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        setActivations(Array.isArray(result.data) ? result.data : []);
      } else {
        setActivationsError("Failed to load activations");
        setActivations([]);
      }
    } catch (error) {
      setActivationsError(
        error instanceof Error ? error.message : "Failed to load activations"
      );
      setActivations([]);
    } finally {
      setActivationsLoading(false);
    }
  };

  // Load data when displayId changes
  useEffect(() => {
    if (displayId) {
      loadDisplay(displayId);
      loadActivations(displayId);
    }
  }, [displayId]);

  // Handle remove activation
  const handleRemoveActivation = (activation: DisplayActivation) => {
    setActivationToRemove(activation);
  };

  // Confirm remove activation
  const handleConfirmRemove = async () => {
    if (!activationToRemove || !activationToRemove.id) return;

    setIsRemoving(true);
    try {
      const result = await DisplayActivationService.remove(
        activationToRemove.id
      );

      if (result.code === OP_RESULT_CODES.OK) {
        // Remove from local state
        setActivations((prev) =>
          prev.filter((a) => a.id !== activationToRemove.id)
        );
        setActivationToRemove(null);
      }
    } catch (error) {
      console.error("Failed to remove activation:", error);
    } finally {
      setIsRemoving(false);
    }
  };

  // Cancel remove
  const handleCancelRemove = () => {
    if (!isRemoving) {
      setActivationToRemove(null);
    }
  };

  // Simple handlers that don't need implementation for now
  const handleSortChange = () => {
    // Not implemented for simplicity
  };

  const handleLoadMore = () => {
    // Not implemented for simplicity
  };

  return (
    <DashboardLayout {...props}>
      <Box>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          {!displayId ? (
            <DisplayLinkError
              title="No Display Selected"
              message="It looks like the display configuration you have selected does not exist anymore."
            />
          ) : (
            <Stack gap="xl">
              {/* Display Header */}
              <DisplayHeader
                display={display}
                loading={displayLoading}
                error={displayError}
              />

              <Space />

              {/* Connected Devices Section */}
              <Stack gap="md">
                <Title order={3}>Connected Devices</Title>

                <DisplayActivationsTable
                  activations={activations}
                  loading={activationsLoading}
                  loadingMore={false}
                  hasMore={false}
                  sortBy={[{ key: "createdAt", direction: "desc" }]}
                  onSortChange={handleSortChange}
                  onLoadMore={handleLoadMore}
                  onRemoveActivation={handleRemoveActivation}
                  removingActivationId={
                    isRemoving && activationToRemove
                      ? activationToRemove.id || null
                      : null
                  }
                  showEmptyState={true}
                />
              </Stack>
            </Stack>
          )}
        </Card>

        {/* Remove Confirmation Modal */}
        <ConfirmModal
          opened={!!activationToRemove}
          onClose={handleCancelRemove}
          onConfirm={handleConfirmRemove}
          title="Remove Device Connection"
          message="Are you sure you want to remove this device connection?"
          details="This will disconnect the device and it will need to be reconnected using a new activation code."
          confirmLabel="Remove"
          type="danger"
          confirmInProgress={isRemoving}
        />
      </Box>
    </DashboardLayout>
  );
};
